import { createFileRoute, useRouter } from '@tanstack/react-router';
import { Badge } from 'components/Badge';
import { Button } from 'components/Button';
import { Heading } from 'components/Heading';
import { Icon } from 'components/Icon';
import { InlineCode } from 'components/InlineCode';
import { ModalButton } from 'components/ModalButton';
import { Para } from 'components/Para';
import { Table } from 'components/Table';
import { emitMessage, useIsAdmin, useToken } from 'engine/api';
import { appConfig } from 'engine/appConfig';
import { GameTypeOptions } from 'forms/GameFormConstants';
import {
	Archive,
	ArchiveRestore,
	ChevronRight,
	Eye,
	Gamepad,
	Layers2,
	Paintbrush,
	Pencil,
	SquareUser,
	Ticket,
	TrendingDown,
	TrendingUp,
	type LucideIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { toSentenceCase } from 'shared/util';

export const Route = createFileRoute('/dashboard/')({
	component: RouteComponent,
	loader: async () => {
		const data = await emitMessage({ kind: 'get-event-list', date: new Date().toISOString() });

		return {
			...(await emitMessage({ kind: 'stats' })),
			variants: data.variantViews ?? [],
			games: data.gameViews ?? [],
			events: data.eventViews ?? [],
		};
	},
});
function RouteComponent() {
	const data = Route.useLoaderData();
	const isAdmin = useIsAdmin();
	const router = useRouter();

	console.log('loaded events:', data.events);

	const today = new Date();
	const eventsToday = data.events
		? data.events.filter((e) => {
				const startDate =
					e.gameOverrides && e.gameOverrides.gameStartsByDate ? new Date(e.gameOverrides.gameStartsByDate) : undefined;
				if (startDate)
					return (
						startDate.getFullYear() === today.getFullYear() &&
						startDate.getMonth() === today.getMonth() &&
						startDate.getDate() === today.getDate()
					);
			})
		: [];

	const { token } = useToken();

	type CardProps = {
		icon: string | LucideIcon;
		label: string;
		href: string;
	};

	type StatProps = {
		icon: string | LucideIcon;
		data: number;
		label: string;
		increase?: number | undefined;
	};

	function Card({ icon, label, href }: CardProps) {
		return (
			<a href={href}>
				<div
					className={`group flex cursor-pointer items-center gap-4 rounded-2xl bg-white p-4 shadow transition-colors duration-200 hover:bg-gradient-to-br hover:from-[var(--theme-500)] hover:to-[var(--theme-600)] hover:text-white dark:bg-gray-800`}
				>
					<div
						className={`rounded-full bg-gray-100 p-3 transition-colors duration-200 group-hover:bg-theme-700 dark:bg-gray-700`}
					>
						<Icon icon={icon} className={`group-hover:text-white`} />
					</div>
					<div>
						<p className="flex text-sm text-gray-500 transition-colors duration-200 group-hover:text-white dark:text-gray-400">
							{label}
							<ChevronRight className="mb-1 size-5" />
						</p>
					</div>
				</div>
			</a>
		);
	}

	const CountUp = ({ target }: { target: number }) => {
		const [count, setCount] = useState(0);

		useEffect(() => {
			let start = 0;
			const step = Math.ceil(target / 40);
			const interval = setInterval(() => {
				start += step;
				if (start >= target) {
					setCount(target);
					clearInterval(interval);
				} else {
					setCount(start);
				}
			}, 30);
			return () => clearInterval(interval);
		}, [target]);

		return <span>{count}</span>;
	};

	function Stat({ icon, data, label, increase }: StatProps) {
		return (
			<div className="flex flex-col items-center gap-4 rounded-2xl bg-white p-4 shadow">
				<div className="rounded-full bg-gray-100 p-3 transition-colors duration-200">
					<Icon icon={icon} className="group-hover:text-white" />
				</div>
				<div className="text-center">
					<p className="text-sm text-gray-500 transition-colors duration-200">
						<CountUp target={data} /> {label}
					</p>
					{increase !== 0 && increase && (
						<p
							className={`flex gap-1 text-xs transition-colors duration-200 ${increase > 0 ? 'text-success-500' : 'text-danger-500'}`}
						>
							{increase > 0 ? <TrendingUp className="size-4" /> : <TrendingDown className="size-4" />}
							<span>
								<CountUp target={increase} /> last 7 days
							</span>
						</p>
					)}
				</div>
			</div>
		);
	}

	function getRandomWelcomeMessage() {
		const welcomeMessages = [
			'Nice to see you again!',
			'Welcome back!',
			"Glad you're here!",
			'Great to have you again!',
			"You're looking sharp today!",
			"Hope you're having a great day!",
		];

		const index = Math.floor(Math.random() * welcomeMessages.length);
		return welcomeMessages[index];
	}

	return (
		<div>
			{token && (
				<>
					<Heading>
						<span className="font-extrabold">Welcome {token.name}</span>
					</Heading>
					<Para className="mb-4">{getRandomWelcomeMessage()}</Para>
				</>
			)}
			<div className="mb-8 mt-2 grid grid-cols-1 gap-4 sm:grid-cols-3 lg:grid-cols-4">
				{isAdmin && <Card icon={Gamepad} label="Games" href="/dashboard/games" />}
				{appConfig.permissions.allowVariants && isAdmin && <Card icon={Layers2} label="Variants" href="/dashboard/variants" />}
				<Card icon={Ticket} label="Events" href="/dashboard/events" />
			</div>

			<Heading mode="tertiary">
				<span className="font-extrabold">Events today:</span>
			</Heading>
			<div className="mb-8 mt-2">
				{!eventsToday && <span>Could not get events.</span>}
				{eventsToday.length > 0 ? (
					<Table
						className="mb-4 w-fit"
						headers={['Name', 'Code', 'Date', 'Game', 'Type', appConfig.permissions.allowVariants ? 'Variant' : '', '']}
						rows={eventsToday.map((e) => {
							const game = data.games?.find((g) => g.id === e.gameId);
							const variant = data.variants?.find((v) => v.id === e.variantId);

							const typeLabel = GameTypeOptions.find((o) => o.value === e.eventType)?.label ?? e.eventType;

							const startsAt = e.gameOverrides?.gameStartsByDate
								? new Date(e.gameOverrides.gameStartsByDate).toLocaleString('nl-NL', {
										day: '2-digit',
										month: '2-digit',
										year: 'numeric',
									})
								: '-';
							const statusLookup = { ended: 'warn', open: 'info', started: 'success', draft: 'info' } as const;
							return [
								// ----------- NAME -----------
								<span className="font-bold">
									{e.name}
									<br />
									<Badge status={statusLookup[e.status]}>{toSentenceCase(e.status)}</Badge>
								</span>,
								// ----------- CODE -----------
								<>
									{e.code && (
										<InlineCode clipboardButton={true} value={e.code}>
											{e.code}
										</InlineCode>
									)}
									{!e.code && <small>Event has no code</small>}
								</>,
								// ----------- STARTS AT -----------
								<span>{startsAt}</span>,
								// ----------- GAME -----------
								<span>{game?.name}</span>,
								// ----------- TYPE -----------
								<span>{typeLabel}</span>,
								// ----------- VARIANT -----------
								appConfig.permissions.allowVariants ? variant ? <span>{variant?.name}</span> : <small>-</small> : <></>,
								// ----------- ACTIONS -----------
								<div className="flex gap-1">
									<Button to={'/dashboard/events/view/$id'} params={{ id: e.id }} mode={'primary'} className="p-1">
										<Eye />
									</Button>
									{isAdmin && (
										<Button
											to={'/dashboard/events/edit/$id'}
											params={{ id: e.id }}
											mode={'secondary'}
											className="border-2 border-gray-200 p-1"
										>
											<Pencil />
										</Button>
									)}
									{isAdmin && (
										<ModalButton
											title={`Are you sure you want to ${!e.archived ? 'archive' : 'unarchive'} "${e.name}"?`}
											onAccept={async () => {
												await emitMessage({ kind: 'event-archive', id: e.id, archive: !e.archived });

												router.navigate({ to: '/dashboard' });
											}}
											mode={'secondary'}
											className="border-2 border-gray-200 p-1"
										>
											{e.archived ? <ArchiveRestore /> : <Archive />}
										</ModalButton>
									)}
								</div>,
							];
						})}
					></Table>
				) : (
					<span>No events today.</span>
				)}
			</div>
			<Heading mode="tertiary">
				<span className="font-extrabold">Statistics:</span>
			</Heading>
			<div className="mb-4 grid grid-cols-2 gap-4 sm:grid-cols-4 md:grid-cols-5">
				<Stat
					icon={Ticket}
					data={data.eventsCount ?? 0}
					label={`Event${data.eventsCount && data.eventsCount > 1 ? 's' : ''}`}
					increase={
						data.events
							? data.events.filter((e) => {
									const startDate = e.createdAt ? new Date(e.createdAt) : undefined;

									if (!startDate) return false;

									const now = new Date();
									const sevenDaysAgo = new Date();
									sevenDaysAgo.setDate(now.getDate() - 7);

									return startDate >= sevenDaysAgo && startDate <= now;
								}).length
							: 0
					}
				/>
				<Stat icon={SquareUser} data={data.teamsCount ?? 0} label={`Team${data.teamsCount && data.teamsCount > 1 ? 's' : ''}`} />
				<Stat
					icon={Gamepad}
					data={data.gamesCount ?? 0}
					label={`Game${data.gamesCount && data.gamesCount > 1 ? 's' : ''}`}
					increase={
						data.games
							? data.games.filter((g) => {
									const startDate = g.createdAt ? new Date(g.createdAt) : undefined;

									if (!startDate) return false;

									const now = new Date();
									const sevenDaysAgo = new Date();
									sevenDaysAgo.setDate(now.getDate() - 7);

									return startDate >= sevenDaysAgo && startDate <= now;
								}).length
							: 0
					}
				/>
				{appConfig.permissions.allowVariants && (
					<Stat
						icon={Layers2}
						data={data.variantsCount ?? 0}
						label={`Variant${data.variantsCount && data.variantsCount > 1 ? 's' : ''}`}
						increase={
							data.variants
								? data.variants.filter((v) => {
										const startDate = v.createdAt ? new Date(v.createdAt) : undefined;

										if (!startDate) return false;

										const now = new Date();
										const sevenDaysAgo = new Date();
										sevenDaysAgo.setDate(now.getDate() - 7);

										return startDate >= sevenDaysAgo && startDate <= now;
									}).length
								: 0
						}
					/>
				)}
				{appConfig.permissions.allowSkins && (
					<Stat
						icon={Paintbrush}
						data={data.skinsCount ?? 0}
						label={`Skin${data.skinsCount && data.skinsCount > 1 ? 's' : ''}`}
					/>
				)}
			</div>
		</div>
	);
}

export default RouteComponent;
